import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PageHeader from '../PageHeader';
import SmartImage from '../ui/SmartImage';

function ArticleLoading() {
    return (
        <main className="min-h-screen bg-slate-50 py-12">
            <div className="container mx-auto max-w-5xl animate-pulse px-4 sm:px-6">
                <div className="h-4 w-32 rounded bg-slate-200" />
                <div className="mt-5 h-10 w-4/5 rounded bg-slate-200" />
                <div className="mt-3 h-10 w-2/3 rounded bg-slate-200" />
                <div className="mt-6 h-4 w-52 rounded bg-slate-200" />
                <div className="mt-8 aspect-[16/8] rounded-3xl bg-slate-200" />
                <div className="mt-8 space-y-3 rounded-3xl bg-white p-8">
                    <div className="h-4 w-full rounded bg-slate-200" />
                    <div className="h-4 w-full rounded bg-slate-200" />
                    <div className="h-4 w-3/4 rounded bg-slate-200" />
                </div>
            </div>
        </main>
    );
}

function MediaDetail() {
    const { slug, jenis } = useParams();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError('');

        fetch(`${process.env.REACT_APP_BACKEND_URL}/articles/slug/${slug}`, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                return response.json();
            })
            .then((data) => setArticle(data))
            .catch((fetchError) => {
                if (fetchError.name !== 'AbortError') {
                    console.error('Error fetching article:', fetchError);
                    setError('Artikel belum dapat dimuat.');
                }
            })
            .finally(() => setLoading(false));

        return () => controller.abort();
    }, [slug]);

    const description = useMemo(() => {
        if (!article) return '';
        const rawContent = typeof article.data_content === 'string' ? article.data_content : '';
        return article.meta_description || article.excerpt || article.description || (rawContent ? rawContent.replace(/<[^>]+>/g, '').slice(0, 160) + '...' : '');
    }, [article]);

    if (loading) {
        return <><PageHeader title="Media" /><ArticleLoading /></>;
    }

    if (error || !article) {
        return <><PageHeader title="Media" /><div className="min-h-[50vh] bg-slate-50 px-4 py-20 text-center text-slate-500">{error || 'Konten tidak ditemukan.'}</div></>;
    }

    const image = article.image_urls?.[0] || 'https://www.len-telko.co.id/logo512.png';
    const contentHtml = typeof article.data_content === 'string'
        ? article.data_content
        : Array.isArray(article.data_content)
            ? article.data_content.filter((item) => typeof item === 'string').join('')
            : '';
    const articleType = article.jenis || jenis || 'berita';
    const canonicalUrl = `https://www.len-telko.co.id/media/${articleType}/${slug}`;

    return (
        <>
            <Helmet>
                <title>{article.title} | PT Len Telekomunikasi Indonesia</title>
                <meta name="description" content={description} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:title" content={article.title} />
                <meta property="og:description" content={description} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:image" content={image} />
                <meta name="twitter:card" content="summary_large_image" />
            </Helmet>
            <PageHeader title="Media" />
            <main className="min-h-screen bg-slate-50 py-10 sm:py-14">
                <article className="container mx-auto max-w-5xl px-4 sm:px-6">
                    <Link to={`/media/${articleType}`} className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-900">&larr; Kembali ke {articleType === 'artikel' ? 'Artikel' : 'Berita'}</Link>

                    <header className="mt-6 overflow-hidden rounded-[30px] bg-gradient-to-br from-slate-950 via-[#0a213a] to-[#07598e] p-7 text-white shadow-[0_22px_60px_rgba(15,23,42,0.18)] sm:p-9 lg:p-11">
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                            <span className="rounded-full bg-blue-500/20 px-3 py-1 font-semibold uppercase tracking-wide text-blue-200">{articleType}</span>
                            {article.published_date && <span className="text-slate-300">{new Date(article.published_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</span>}
                        </div>
                        <h1 className="mt-5 text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-5xl">{article.title}</h1>
                        {article.author && <p className="mt-5 text-sm font-medium text-slate-300">{article.author}</p>}
                    </header>

                    {article.image_urls?.[0] && (
                        <SmartImage
                            src={article.image_urls[0]}
                            alt={article.title}
                            wrapperClassName="mt-7 aspect-[16/8] w-full rounded-[28px] border border-slate-200 bg-slate-200 shadow-sm"
                            className="h-full w-full object-cover"
                        />
                    )}

                    <div className="mt-7 rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_18px_48px_rgba(15,23,42,0.06)] sm:p-8 lg:p-10">
                        {article.description && (
                            <p className="mb-7 border-l-4 border-blue-500 pl-5 text-base italic leading-8 text-slate-500">{article.description}</p>
                        )}
                        <div
                            className="lti-rich-content text-base leading-8 text-slate-700 [&_h1]:mb-4 [&_h1]:mt-8 [&_h1]:text-3xl [&_h1]:font-bold [&_h2]:mb-4 [&_h2]:mt-8 [&_h2]:text-2xl [&_h2]:font-bold [&_h3]:mb-3 [&_h3]:mt-7 [&_h3]:text-xl [&_h3]:font-bold [&_img]:mx-auto [&_img]:my-8 [&_img]:rounded-2xl [&_img]:shadow-sm [&_ol]:mb-6 [&_ol]:list-decimal [&_ol]:space-y-2 [&_ol]:pl-6 [&_p]:mb-6 [&_strong]:font-bold [&_strong]:text-slate-950 [&_ul]:mb-6 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-6 sm:text-lg"
                            dangerouslySetInnerHTML={{ __html: contentHtml || '<p>Konten belum tersedia.</p>' }}
                        />
                    </div>

                    {article.image_urls?.length > 1 && (
                        <div className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {article.image_urls.slice(1).map((url, index) => (
                                <SmartImage key={url} src={url} alt={`${article.title} - ${index + 2}`} wrapperClassName="aspect-[4/3] rounded-2xl bg-slate-200" className="h-full w-full object-cover" />
                            ))}
                        </div>
                    )}
                </article>
            </main>
        </>
    );
}

export default MediaDetail;
